import { useMemo } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import StructuredData from "@/components/feature/StructuredData";
import PageMeta from "@/components/feature/PageMeta";
import BangaloreCoverageMap, { CoverageLocalitiesList } from "@/pages/services/components/BangaloreCoverageMap";
import { areaDetails } from "@/mocks/areas";
import { buildAreaListSchema, buildBreadcrumbSchema } from "@/utils/schemaBuilders";
import { resolveSeo } from "@/utils/seo";
import { HERO_IMAGES, AREA_THUMB_IMAGES } from "@/config/heroImages";

const CTA_IMAGE =
  "/uploads/hero-fleet-banner.jpeg";

export default function AreasPage() {
  const breadcrumbSchema = useMemo(
    () =>
      buildBreadcrumbSchema([
        { label: "Home", href: "/" },
        { label: "Areas we Serve" },
      ]),
    [],
  );

  const listSchema = useMemo(
    () => buildAreaListSchema(areaDetails.map((area) => ({ name: area.name, slug: area.slug }))),
    [],
  );

  // Reuse the central SEO config so the title/description stay within limits.
  const seo = useMemo(() => resolveSeo("/areas-we-serve"), []);

  return (
    <>
      <PageMeta
        title={seo.title}
        description={seo.description}
        path="/areas-we-serve"
        image={CTA_IMAGE}
      />
      <StructuredData id="ld-areas-breadcrumb" data={breadcrumbSchema} />
      <StructuredData id="ld-areas-list" data={listSchema} />

      <PageHero
        eyebrow="Areas we Serve"
        title="Local Packers and Movers Across Bangalore"
        subtitle="From Nagarabhavi in the west to Whitefield in the east, our local shifting crews cover every major residential locality in Bangalore. Pick your neighbourhood to see how we help families move within it."
        crumbs={[{ label: "Home", href: "/" }, { label: "Areas we Serve" }]}
        image={HERO_IMAGES.areas}
      />

      {/* Intro */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 items-stretch gap-10 px-4 md:px-6 lg:grid-cols-2 lg:gap-14">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Your Neighbourhood Crew"
              title="We move families within their own city, area by area"
            />
            <div className="mt-5 space-y-4">
              <p className="text-sm leading-relaxed text-foreground-600 md:text-base">
                Local shifting is our speciality. Because we know Bangalore's traffic patterns, apartment rules, lift
                timings and parking access, we plan every move for the fastest route and the shortest downtime for your
                family. Most within-city shifts are packed, moved and unpacked in a single day.
              </p>
              <p className="text-sm leading-relaxed text-foreground-600 md:text-base">
                Our crews also know each locality's building requirements, so you get fewer surprises on moving day.
                Tap any area below to see the kind of local shifting we handle there, along with what customers in that
                neighbourhood say about us.
              </p>
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/services/local-shifting"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-route-line text-lg" aria-hidden="true" /> Explore Local Shifting
              </Link>
              <Link
                to="/request-a-quote"
                data-cta="Get a Free Quote"
                data-cta-location="areas-intro"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-file-text-line text-lg" aria-hidden="true" /> Get a Free Quote
              </Link>
            </div>

            <div className="mt-8">
              <CoverageLocalitiesList />
            </div>
          </div>

          <div className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h3 className="font-heading text-base font-semibold text-foreground-950">
              Pinned coverage at a glance
            </h3>
            <p className="mt-1.5 text-sm text-foreground-600">
              Where our local moving crews are most active across the city.
            </p>
            <div className="mt-4 flex-1">
              <BangaloreCoverageMap showLocalities={false} fillMapHeight />
            </div>
          </div>
        </div>
      </section>

      {/* All localities */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="All Localities"
            title="Every Bangalore locality we cover"
            subtitle="Pick your area to open its dedicated local shifting page."
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {areaDetails.map((area) => (
              <Link
                key={area.slug}
                to={`/areas-we-serve/${area.slug}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300"
              >
                <div className="aspect-[4/3] w-full overflow-hidden">
                  <img
                    src={AREA_THUMB_IMAGES[area.slug]}
                    alt={`Packers and Movers in ${area.name}, Bangalore`}
                    title={`Packers and Movers in ${area.name}, Bangalore - Lahari`}
                    className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                      <i className="ri-map-pin-2-line" aria-hidden="true" />
                      {area.region}
                    </span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-arrow-right-up-line text-lg" aria-hidden="true" />
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                    Packers and Movers in {area.name}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{area.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                    View {area.name} services
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}